<template>
  <v-container fluid grid-list-md>
    <v-layout row wrap align-center>
      <v-flex xs12 sm6>
        <h2 class="headline">Contractor Faults</h2>
        <div class="grey--text body-1">Faults the network operator has referred to you.</div>
      </v-flex>
      <v-flex xs12 sm6 class="text-sm-right">
        <v-btn flat color="primary" :loading="loading" @click="loadFaults">
          <v-icon left>refresh</v-icon>Refresh
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 sm4>
        <v-select :items="filters" v-model="filter" label="Show"></v-select>
      </v-flex>
      <v-flex xs12 sm3>
        <v-checkbox v-model="myJobs" label="My jobs (and unassigned)" hide-details></v-checkbox>
      </v-flex>
      <v-flex xs12 sm5>
        <v-text-field v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
      </v-flex>
    </v-layout>

    <!-- Phone: one card per job, tap to open. -->
    <div v-if="isPhone">
      <div v-if="loading && !faults.length" class="grey--text body-1 pa-2">Loading&hellip;</div>
      <div v-else-if="!phoneFaults.length" class="text-xs-center pa-3 grey--text">No faults referred to you.</div>
      <v-card v-for="f in phoneFaults" :key="f.id" class="mb-2" style="cursor:pointer" @click.native="openDetail(f)">
        <v-card-text class="pa-3">
          <v-layout row align-center>
            <strong>{{ f.reference }}</strong>
            <v-spacer></v-spacer>
            <v-chip small :color="referralColour(f.referralStatus)" text-color="white">{{ f.referralStatus }}</v-chip>
          </v-layout>
          <div class="subheading">{{ f.estate || '-' }} &middot; Unit {{ f.unitReference || '-' }}</div>
          <div>{{ f.title }}</div>
          <div class="caption"><v-icon small>person</v-icon> {{ f.assignedName || 'Not assigned' }}</div>
          <div class="caption grey--text">
            Referred {{ formatDate(f.referredAt) }}<span v-if="f.priority"> &middot; {{ f.priority }}</span><span v-if="f.faultStatus === 'Resolved'"> &middot; Fault closed</span>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-data-table v-else :headers="headers" :items="shownFaults" :search="search" :loading="loading" class="elevation-1">
      <template slot="items" slot-scope="props">
        <td>{{ props.item.reference }}</td>
        <td>{{ props.item.title }}</td>
        <td>{{ props.item.estate || '-' }}</td>
        <td>{{ props.item.unitReference || '-' }}</td>
        <td>{{ props.item.priority || '-' }}</td>
        <td>{{ props.item.assignedName || '-' }}</td>
        <td>
          <v-chip small :color="referralColour(props.item.referralStatus)" text-color="white">{{ props.item.referralStatus }}</v-chip>
          <div v-if="props.item.faultStatus === 'Resolved'" class="caption grey--text">Fault closed</div>
        </td>
        <td>{{ formatDate(props.item.referredAt) }}</td>
        <td class="text-xs-right">
          <v-btn flat small color="primary" @click="openDetail(props.item)">Open</v-btn>
        </td>
      </template>
      <template slot="no-data">
        <div class="text-xs-center pa-3 grey--text">No faults referred to you.</div>
      </template>
    </v-data-table>

    <v-dialog v-model="detailDialog" max-width="800px" :fullscreen="isPhone" :transition="isPhone ? 'dialog-bottom-transition' : 'dialog-transition'">
      <v-card v-if="selected">
        <v-toolbar v-if="isPhone" dark color="primary" dense>
          <v-btn icon dark @click="detailDialog = false"><v-icon>close</v-icon></v-btn>
          <v-toolbar-title>{{ selected.reference }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip v-if="detail" small :color="referralColour(detail.referral.status)" text-color="white">{{ detail.referral.status }}</v-chip>
        </v-toolbar>
        <div v-if="isPhone" class="title px-3 pt-3">{{ selected.title }}</div>
        <v-card-title v-else class="title">
          {{ selected.reference }} &mdash; {{ selected.title }}
          <v-spacer></v-spacer>
          <v-chip v-if="detail" small :color="referralColour(detail.referral.status)" text-color="white">{{ detail.referral.status }}</v-chip>
        </v-card-title>
        <v-card-text>
          <div v-if="detailLoading" class="grey--text body-1">Loading&hellip;</div>
          <div v-else-if="detail">
            <div v-if="detail.fault.status === 'Resolved'" class="green--text body-1 mb-2">
              The network operator has closed this fault.
            </div>
            <div v-if="detail.referral.note" class="mb-3">
              <strong>Instructions from {{ detail.fault.fnoName || 'the network operator' }}</strong>
              <div>{{ detail.referral.note }}</div>
            </div>

            <v-layout row wrap>
              <v-flex xs12 sm6><strong>Estate:</strong> {{ detail.fault.estate || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>Unit:</strong> {{ detail.fault.unitReference || '-' }}</v-flex>
              <v-flex xs12><strong>Address:</strong> {{ addressText || '-' }}
                <a v-if="detail.address && detail.address.coordinates" target="_blank" rel="noopener"
                   :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(detail.address.coordinates)">Map</a>
              </v-flex>
              <v-flex xs12 sm6><strong>ONT:</strong>
                <span v-if="detail.ont">{{ detail.ont.note || '-' }}<span v-if="detail.ont.serialNumber"> &middot; {{ detail.ont.serialNumber }}</span><span v-if="detail.ont.model"> &middot; {{ detail.ont.model }}</span></span>
                <span v-else>-</span>
              </v-flex>
              <v-flex xs12 sm6><strong>Circuit:</strong> {{ detail.fault.circuitNumber || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>Category:</strong> {{ detail.fault.category || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>Priority:</strong> {{ detail.fault.priority || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>ISP:</strong> {{ detail.fault.ispName || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>Package:</strong>
                <span v-if="detail.customer && detail.customer.packageName">{{ detail.customer.packageName }}<span v-if="detail.customer.downloadMbps"> &middot; {{ detail.customer.downloadMbps }}/{{ detail.customer.uploadMbps }} Mbps</span></span>
                <span v-else>-</span>
              </v-flex>
            </v-layout>

            <v-divider class="my-3"></v-divider>
            <strong>Client</strong>
            <v-layout row wrap>
              <v-flex xs12 sm6><strong>Name:</strong> {{ (detail.customer && detail.customer.name) || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>Mobile:</strong>
                <a v-if="detail.customer && detail.customer.mobileNumber" :href="'tel:' + detail.customer.mobileNumber">{{ detail.customer.mobileNumber }}</a>
                <span v-else>-</span>
              </v-flex>
              <v-flex xs12 sm6 v-if="detail.customer && detail.customer.telephoneNumber"><strong>Telephone:</strong>
                <a :href="'tel:' + detail.customer.telephoneNumber">{{ detail.customer.telephoneNumber }}</a>
              </v-flex>
              <v-flex xs12 sm6><strong>Email:</strong> {{ (detail.customer && detail.customer.emailAddress) || '-' }}</v-flex>
              <v-flex xs12 sm6><strong>Site contact:</strong> {{ detail.fault.contactName || '-' }}
                <a v-if="detail.fault.contactNumber" :href="'tel:' + detail.fault.contactNumber">{{ detail.fault.contactNumber }}</a>
              </v-flex>
            </v-layout>

            <div class="mt-3"><strong>Description</strong></div>
            <div>{{ detail.fault.description || '-' }}</div>
            <div class="caption grey--text">Logged {{ formatDate(detail.fault.createdAt) }}</div>

            <div class="mt-3"><strong>Photos on the fault</strong></div>
            <div class="mt-1">
              <span v-if="!detail.faultAttachments.length" class="grey--text caption">None.</span>
              <v-avatar v-for="a in detail.faultAttachments" :key="'f' + a.id" tile size="96" class="mr-2 mb-2"
                        style="cursor:pointer;border:1px solid #ddd" @click="openPhoto('f' + a.id)">
                <img v-if="thumbs['f' + a.id]" :src="thumbs['f' + a.id]" style="object-fit:cover">
                <v-progress-circular v-else indeterminate size="20" color="grey"></v-progress-circular>
              </v-avatar>
            </div>

            <v-divider class="my-3"></v-divider>

            <div v-if="detail.canAct" class="mb-2">
              <strong>Technician</strong>
              <v-select :items="teamItems" item-text="name" item-value="id" v-model="assignTo"
                        label="Assigned to" :loading="assigning" @change="assignJob"></v-select>
            </div>
            <div v-else-if="detail.referral.assignedName" class="mb-2">
              <strong>Technician:</strong> {{ detail.referral.assignedName }}
            </div>

            <div v-if="detail.canAct">
              <strong>Update the job</strong>
              <v-text-field v-model="statusNote" :label="noteLabel" maxlength="1000"></v-text-field>
              <v-btn :small="!isPhone" :large="isPhone" :block="isPhone" color="blue" dark v-if="detail.referral.status === 'Referred'"
                     :loading="savingStatus === 'Accepted'" @click="setStatus('Accepted')">Accept</v-btn>
              <v-btn :small="!isPhone" :large="isPhone" :block="isPhone" color="orange" dark v-if="detail.referral.status !== 'On site'"
                     :loading="savingStatus === 'On site'" @click="setStatus('On site')">On site</v-btn>
              <v-btn :small="!isPhone" :large="isPhone" :block="isPhone" color="green" dark :loading="savingStatus === 'Completed'" @click="setStatus('Completed')">Completed</v-btn>
              <v-btn :small="!isPhone" :large="isPhone" :block="isPhone" flat color="error" :loading="savingStatus === 'Returned'" @click="setStatus('Returned')">Return to operator</v-btn>
              <div class="caption grey--text">
                Completed tells the network operator the work is done; they close the fault with the ISP.
                Return it if you cannot fix it, and say why.
              </div>
              <v-divider class="my-3"></v-divider>
            </div>

            <strong>Messages with {{ detail.fault.fnoName || 'the network operator' }}</strong>
            <div class="caption grey--text mb-2">Only you and the network operator see these.</div>
            <div v-if="!detail.comments.length" class="grey--text body-1 mb-2">No messages yet.</div>
            <div v-for="c in detail.comments" :key="c.id" class="mb-2">
              <div class="caption grey--text">
                {{ c.authorSide === 'System' ? 'Status update' : (c.authorSide === 'FNO' ? (detail.fault.fnoName || 'Network operator') : 'You') }}
                <span v-if="c.authorName">&middot; {{ c.authorName }}</span>
                &middot; {{ formatDate(c.createdAt) }}
              </div>
              <div :class="c.authorSide === 'System' ? 'grey--text' : ''">{{ c.comment }}</div>
            </div>
            <div class="mt-2">
              <v-avatar v-for="a in detail.attachments" :key="'c' + a.id" tile size="96" class="mr-2 mb-2"
                        style="cursor:pointer;border:1px solid #ddd" @click="openPhoto('c' + a.id)">
                <img v-if="thumbs['c' + a.id]" :src="thumbs['c' + a.id]" style="object-fit:cover">
                <v-progress-circular v-else indeterminate size="20" color="grey"></v-progress-circular>
              </v-avatar>
            </div>
            <div v-if="detail.canComment">
              <v-textarea v-model="newComment" label="Message" rows="2"></v-textarea>
              <v-btn :small="!isPhone" :block="isPhone" color="primary" @click="addComment" :loading="savingComment" :disabled="!newComment">Post</v-btn>
              <input ref="fileInput" type="file" accept="image/*" multiple style="display:none" @change="onPickFiles($event)">
              <!-- capture opens the phone's camera straight away; desktops ignore it. -->
              <input ref="cameraInput" type="file" accept="image/*" capture="environment" style="display:none" @change="onPickFiles($event)">
              <v-btn :small="!isPhone" :block="isPhone" :outline="isPhone" :flat="!isPhone" color="primary" :loading="uploading" @click="$refs.cameraInput.click()">
                <v-icon left small>photo_camera</v-icon>Take photo
              </v-btn>
              <v-btn :small="!isPhone" :block="isPhone" flat color="primary" :loading="uploading" @click="$refs.fileInput.click()">
                <v-icon left small>attach_file</v-icon>{{ isPhone ? 'Add from gallery' : 'Add photos' }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewer" max-width="900px" :fullscreen="isPhone">
      <v-card>
        <v-card-text class="text-xs-center pa-2">
          <img :src="viewerSrc" style="max-width:100%;max-height:78vh">
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="viewer = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack" :timeout="4000">{{ snackText }}</v-snackbar>
  </v-container>
</template>

<script>
import Api from "@/services/Api";

// The contractor's side of fault referrals (faultReferralController). Only ever
// shows faults referred to the caller's organisation; the server enforces it.
export default {
  name: "ContractorFaults",
  data() {
    return {
      faults: [],
      loading: false,
      search: "",
      filter: "Open jobs",
      // Technicians start on their own jobs; untick to see the whole company's.
      myJobs: true,
      team: [],
      assignTo: null,
      assigning: false,
      filters: ["Open jobs", "All"],
      headers: [
        { text: "Reference", value: "reference" },
        { text: "Summary", value: "title" },
        { text: "Estate", value: "estate" },
        { text: "Unit", value: "unitReference" },
        { text: "Priority", value: "priority" },
        { text: "Technician", value: "assignedName" },
        { text: "Status", value: "referralStatus" },
        { text: "Referred", value: "referredAt" },
        { text: "", value: "id", sortable: false, align: "right" }
      ],
      selected: null,
      detail: null,
      detailLoading: false,
      detailDialog: false,
      statusNote: "",
      savingStatus: "",
      newComment: "",
      savingComment: false,
      uploading: false,
      thumbs: {},
      viewer: false,
      viewerSrc: "",
      snack: false,
      snackText: "",
      pollTimer: null
    };
  },
  computed: {
    // Phones and small tablets get the card list and full-screen job page.
    isPhone() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    // The table does its own searching; the phone cards are filtered here.
    phoneFaults() {
      const q = (this.search || "").toLowerCase().trim();
      if (!q) return this.shownFaults;
      return this.shownFaults.filter(f =>
        [f.reference, f.title, f.estate, f.unitReference, f.referralStatus].some(
          v => v && String(v).toLowerCase().indexOf(q) !== -1
        )
      );
    },
    teamItems() {
      return [{ id: null, name: "Not assigned" }].concat(this.team);
    },
    shownFaults() {
      const me = this.$store.state.user && this.$store.state.user.id;
      const base = this.myJobs ? this.faults.filter(f => !f.assignedContactId || f.assignedContactId === me) : this.faults;
      if (this.filter === "All") return base;
      return base.filter(
        f => ["Referred", "Accepted", "On site"].indexOf(f.referralStatus) !== -1 && f.faultStatus !== "Resolved"
      );
    },
    addressText() {
      const a = this.detail && this.detail.address;
      if (!a) return "";
      return [
        a.unitNo ? "Unit " + a.unitNo : null,
        a.locationDescription,
        a.complexName,
        a.complexStreetName,
        a.networkName,
        [a.streetNumber, a.streetName].filter(Boolean).join(" "),
        a.suburb,
        a.town
      ].filter(Boolean).join(", ");
    },
    noteLabel() {
      return "Note (required when returning)";
    }
  },
  mounted() {
    this.loadFaults();
    this.loadTeam();
    this.pollTimer = setInterval(() => {
      if (document.visibilityState !== "visible" || this.loading || this.detailDialog) return;
      this.loadFaults();
    }, 60000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    loadFaults() {
      this.loading = true;
      Api()
        .get("/contractorFaults")
        .then(response => {
          this.faults = response.data || [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.notify("Could not load faults.");
        });
    },
    openDetail(item) {
      this.selected = item;
      this.detail = null;
      this.thumbs = {};
      this.statusNote = "";
      this.newComment = "";
      this.detailDialog = true;
      this.loadDetail();
    },
    loadDetail() {
      const faultId = this.selected.id;
      this.detailLoading = !this.detail;
      Api()
        .get("/contractorFaults/" + faultId)
        .then(response => {
          if (!this.selected || this.selected.id !== faultId) return;
          this.detail = response.data;
          this.detailLoading = false;
          this.assignTo = this.detail.referral.assignedContactId || null;
          (this.detail.faultAttachments || []).forEach(a => this.loadThumb(faultId, a.id, "fault"));
          (this.detail.attachments || []).forEach(a => this.loadThumb(faultId, a.id, "contractor"));
        })
        .catch(() => {
          this.detailLoading = false;
          this.notify("Could not load the fault.");
        });
    },
    loadTeam() {
      Api()
        .get("/contractorFaults/team")
        .then(response => {
          this.team = response.data || [];
        })
        .catch(() => {});
    },
    assignJob() {
      if (!this.selected) return;
      this.assigning = true;
      Api()
        .put("/contractorFaults/" + this.selected.id + "/assign", { contactId: this.assignTo })
        .then(() => {
          this.assigning = false;
          this.notify(this.assignTo ? "Job assigned." : "Technician cleared.");
          this.loadDetail();
          this.loadFaults();
        })
        .catch(error => {
          this.assigning = false;
          this.notify(
            (error.response && error.response.data && error.response.data.error) || "Could not assign the job."
          );
          this.assignTo = this.detail ? this.detail.referral.assignedContactId || null : null;
        });
    },
    loadThumb(faultId, id, kind) {
      const key = (kind === "fault" ? "f" : "c") + id;
      if (this.thumbs[key]) return;
      Api()
        .get("/faultReferral/" + faultId + "/attachments/" + id + "?kind=" + kind)
        .then(response => {
          this.$set(this.thumbs, key, response.data);
        })
        .catch(() => {});
    },
    openPhoto(key) {
      if (!this.thumbs[key]) return;
      this.viewerSrc = this.thumbs[key];
      this.viewer = true;
    },
    setStatus(status) {
      if (status === "Returned" && !this.statusNote) {
        this.notify("Say why you are returning the fault.");
        return;
      }
      if (status === "Completed" && !confirm("Mark the work as completed?")) return;
      this.savingStatus = status;
      Api()
        .put("/contractorFaults/" + this.selected.id + "/status", { status: status, note: this.statusNote })
        .then(() => {
          this.savingStatus = "";
          this.statusNote = "";
          this.notify("Status updated: " + status + ".");
          this.loadDetail();
          this.loadFaults();
        })
        .catch(error => {
          this.savingStatus = "";
          this.notify(
            (error.response && error.response.data && error.response.data.error) || "Could not update the status."
          );
        });
    },
    addComment() {
      if (!this.newComment) return;
      this.savingComment = true;
      Api()
        .post("/faultReferral/" + this.selected.id + "/comments", { comment: this.newComment })
        .then(() => {
          this.savingComment = false;
          this.newComment = "";
          this.loadDetail();
        })
        .catch(error => {
          this.savingComment = false;
          this.notify(
            (error.response && error.response.data && error.response.data.error) || "Could not post the message."
          );
        });
    },
    // Same browser-side resize as the fault page: longest edge 1600px, JPEG 0.8.
    resizeImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("read failed"));
        reader.onload = () => {
          const img = new Image();
          img.onerror = () => reject(new Error("decode failed"));
          img.onload = () => {
            const MAX = 1600;
            let w = img.width;
            let h = img.height;
            if (w > MAX || h > MAX) {
              const s = Math.min(MAX / w, MAX / h);
              w = Math.round(w * s);
              h = Math.round(h * s);
            }
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },
    async onPickFiles(event) {
      const files = Array.prototype.slice.call(event.target.files || []);
      event.target.value = "";
      if (!files.length) return;
      this.uploading = true;
      for (const f of files.filter(x => x.size <= 25 * 1024 * 1024)) {
        try {
          const data = await this.resizeImage(f);
          await Api().post("/faultReferral/" + this.selected.id + "/attachments", { name: f.name, data: data });
        } catch (e) {
          this.notify("Could not upload " + f.name);
        }
      }
      this.uploading = false;
      this.loadDetail();
    },
    referralColour(status) {
      if (status === "Completed") return "green";
      if (status === "Returned") return "red";
      if (status === "On site") return "orange";
      if (status === "Re-assigned") return "grey";
      return "blue";
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-ZA");
    },
    notify(text) {
      this.snackText = text;
      this.snack = true;
    }
  }
};
</script>
